import React, { useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
export function WeightLoss() {
  const containerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleMouseMove = e => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      container.style.setProperty('--mouse-x', x);
      container.style.setProperty('--mouse-y', y);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  return <div ref={containerRef} className="min-h-screen w-full" style={{
    backgroundImage: 'radial-gradient(circle at calc(var(--mouse-x, 0.5) * 100%) calc(var(--mouse-y, 0.5) * 100%), rgba(187, 221, 255, 0.35), transparent 50%)',
    backgroundSize: '120% 120%',
    backgroundPosition: 'center'
  }} data-id="element-812">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden" data-id="element-813">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-814">
          <div className="absolute -top-[30%] -right-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-[#BBDDFF]/20 to-transparent blur-3xl" data-id="element-815"></div>
          <div className="absolute -bottom-[30%] -left-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-tr from-[#88AAFF]/10 to-transparent blur-3xl" data-id="element-816"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-817">
          <div className="max-w-4xl mx-auto" data-id="element-818">
            <div className="inline-block mb-6 px-4 py-1 rounded-full bg-gradient-to-r from-[#BBDDFF]/20 to-[#C85A15]/20 backdrop-blur-sm border border-white/10" data-id="element-819">
              <span className="text-sm font-medium bg-gradient-to-r from-[#BBDDFF] to-[#C85A15] bg-clip-text text-transparent" data-id="element-820">
                Weight Loss Treatment
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-821">
              <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-822">
                Clinical Weight Loss
              </span>
              <br data-id="element-823" />
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent" data-id="element-824">
                That Actually Works
              </span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-10" data-id="element-825">
              Medically supervised weight loss programs with GLP-1 medications,
              personalized nutrition guidance, and continuous support from
              dedicated healthcare providers.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-826">
              <a href="https://staging.joinomu.com/patient/onboarding" target="_blank" rel="noopener noreferrer" className="flex items-center bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] text-white font-medium py-3 px-8 rounded-full hover:shadow-lg transition-all group" data-id="element-827">
                Start Your Treatment Today
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} data-id="element-828" />
              </a>
              <Link to="/mens-health" className="w-full sm:w-auto bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 font-medium py-3 px-8 rounded-full hover:shadow-lg transition-all text-center" data-id="element-829">
                Explore Other Treatments
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Treatment Details */}
      <section className="py-20 relative overflow-hidden" data-id="element-830">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-[#BBDDFF]/10 blur-3xl" data-id="element-831"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-[#88AAFF]/10 blur-3xl" data-id="element-832"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-833">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-834">
            <div data-id="element-835">
              <div className="relative" data-id="element-836">
                <div className="absolute inset-0 bg-gradient-to-r from-[#BBDDFF] to-[#BBDDFF]/70 rounded-2xl blur-sm opacity-70" data-id="element-837"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700/30 overflow-hidden" data-id="element-838">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#BBDDFF]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" data-id="element-839"></div>
                  <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Weight Loss Treatment - Diverse group of women supporting each other" className="w-full h-auto object-cover rounded-xl mb-8" data-id="element-840" />
                  <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-841">
                    Personalized GLP-1 Medication Plans
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-842">
                    Our medical providers create customized treatment plans
                    using FDA-approved GLP-1 medications like Wegovy, Saxenda,
                    and Ozempic, tailored to your specific health needs and
                    goals.
                  </p>
                  <ul className="space-y-3 mb-8" data-id="element-843">
                    <li className="flex items-start" data-id="element-844">
                      <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-845" />
                      <span data-id="element-846">
                        Prescription medications delivered to your door
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-847">
                      <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-848" />
                      <span data-id="element-849">Dosage adjustments based on your progress</span>
                    </li>
                    <li className="flex items-start" data-id="element-850">
                      <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-851" />
                      <span data-id="element-852">
                        Medical monitoring to ensure safety and efficacy
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-853">
                      <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-854" />
                      <span data-id="element-855">
                        Prescription strength weight loss medication packs
                        available to those not eligible for GLP-1 medications.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div data-id="element-856">
              <h2 className="text-3xl font-bold mb-6" data-id="element-857">
                <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-858">
                  A comprehensive approach to weight loss
                </span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-859">
                JoinOmu's weight loss program goes beyond medication. Our
                holistic approach combines medical expertise, nutritional
                guidance, and continuous support to help you achieve sustainable
                results.
              </p>
              <div className="space-y-6 mb-8" data-id="element-860">
                <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 shadow-md border border-gray-100 dark:border-gray-700/30" data-id="element-861">
                  <h3 className="text-xl font-semibold mb-3 flex items-center" data-id="element-862">
                    <CheckCircle className="text-[#BBDDFF] mr-3 flex-shrink-0" size={24} data-id="element-863" />
                    Dedicated Medical Provider
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-864">
                    Work with a healthcare provider who specializes in weight
                    management who's recognized by their peers for exellence in
                    their field.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 shadow-md border border-gray-100 dark:border-gray-700/30" data-id="element-865">
                  <h3 className="text-xl font-semibold mb-3 flex items-center" data-id="element-866">
                    <CheckCircle className="text-[#BBDDFF] mr-3 flex-shrink-0" size={24} data-id="element-867" />
                    Personalized Medications
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-868">
                    Receive presonalized GLP-1 treatments or customizable
                    prescription strength medication packs delivered to your
                    door based on your goals and eligibility.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 shadow-md border border-gray-100 dark:border-gray-700/30" data-id="element-869">
                  <h3 className="text-xl font-semibold mb-3 flex items-center" data-id="element-870">
                    <CheckCircle className="text-[#BBDDFF] mr-3 flex-shrink-0" size={24} data-id="element-871" />
                    Continuous Monitoring & Support
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-872">
                    Track your progress through our platform including
                    medication tracking and wearable health metrics. Receive
                    ongoing support with unlimited messaging and regular
                    check-ins.
                  </p>
                </div>
              </div>
              <a href="https://staging.joinomu.com/patient/onboarding" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] text-white font-medium py-3 px-10 rounded-full hover:shadow-lg transition-all text-lg" data-id="element-873">
                Verify eligibility
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Success Stories */}
      <section className="hidden py-24 relative overflow-hidden" data-id="element-874">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-[#BBDDFF]/10 blur-3xl" data-id="element-875"></div>
        <div className="absolute -bottom-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-[#C85A15]/5 blur-3xl" data-id="element-876"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-877">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-878">
            <div className="inline-block mb-4 px-4 py-1 rounded-full bg-gradient-to-r from-[#BBDDFF]/10 to-[#C85A15]/10 backdrop-blur-sm border border-white/10" data-id="element-879">
              <span className="text-sm font-medium" data-id="element-880">Success Stories</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-881">
              <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-882">
                Real patients, real results
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-883">
              Hear from JoinOmu patients who have transformed their lives
              through our weight loss program.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-884">
            {/* Testimonial 1 */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700/30 relative" data-id="element-885">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#BBDDFF]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-xl" data-id="element-886"></div>
              <div className="flex items-center mb-6" data-id="element-887">
                <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Patient" className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-[#BBDDFF]" data-id="element-888" />
                <div data-id="element-889">
                  <h4 className="font-semibold" data-id="element-890">Sarah K.</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-891">
                    Lost 45 pounds in 6 months
                  </p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-892">
                "JoinOmu changed my life. The combination of medication and
                continuous support from my provider made all the difference. For
                the first time, I feel in control of my eating habits."
              </p>
              <div className="flex items-center" data-id="element-893">
                <div className="flex" data-id="element-894">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-895">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-896"></path>
                    </svg>)}
                </div>
              </div>
            </div>
            {/* Testimonial 2 */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700/30 relative" data-id="element-897">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#BBDDFF]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-xl" data-id="element-898"></div>
              <div className="flex items-center mb-6" data-id="element-899">
                <img src="https://images.unsplash.com/photo-1552058544-f2b08422138a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=699&q=80" alt="Patient" className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-[#BBDDFF]" data-id="element-900" />
                <div data-id="element-901">
                  <h4 className="font-semibold" data-id="element-902">James T.</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-903">
                    Lost 60 pounds in 8 months
                  </p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-904">
                "After years of yo-yo dieting, JoinOmu's medical approach was
                exactly what I needed. The GLP-1 medication reduced my cravings,
                and my provider helped me develop healthier habits."
              </p>
              <div className="flex items-center" data-id="element-905">
                <div className="flex" data-id="element-906">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-907">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-908"></path>
                    </svg>)}
                </div>
              </div>
            </div>
            {/* Testimonial 3 */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700/30 relative" data-id="element-909">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#BBDDFF]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-xl" data-id="element-910"></div>
              <div className="flex items-center mb-6" data-id="element-911">
                <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80" alt="Patient" className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-[#BBDDFF]" data-id="element-912" />
                <div data-id="element-913">
                  <h4 className="font-semibold" data-id="element-914">Maria L.</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-915">
                    Lost 35 pounds in 5 months
                  </p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-916">
                "The convenience of JoinOmu made all the difference for me. I
                could message my doctor anytime, and medication was delivered
                right to my door. This program fits perfectly into my busy
                life."
              </p>
              <div className="flex items-center" data-id="element-917">
                <div className="flex" data-id="element-918">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-919">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-920"></path>
                    </svg>)}
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-12" data-id="element-921">
            <button className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 font-medium py-3 px-8 rounded-full hover:shadow-lg transition-all" data-id="element-922">
              View More Success Stories
            </button>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" data-id="element-923">
        <div className="absolute inset-0 bg-gradient-to-r from-[#BBDDFF]/30 to-[#88AAFF]/30 dark:from-[#BBDDFF]/10 dark:to-[#88AAFF]/10" data-id="element-924"></div>
        <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-[#BBDDFF]/15 blur-3xl" data-id="element-925"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-[#88AAFF]/15 blur-3xl" data-id="element-926"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-927">
          <div className="max-w-4xl mx-auto text-center" data-id="element-928">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-929">
              <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-930">
                Ready to start your weight loss journey?
              </span>
            </h2>

            <a href="https://staging.joinomu.com/patient/onboarding" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] text-white font-medium py-3 px-10 rounded-full hover:shadow-lg transition-all text-lg" data-id="element-931">
              Verify eligibility
            </a>
          </div>
        </div>
      </section>
    </div>;
}